export default {
  name: "W.I.P. Installation",
  tags: ["coding", "physical", "art", "3D", "fabrication"],
  description: "A large projection-mapped sculptural installation using Rhino 3D, Grasshopper, and TouchDesigner",
  hash: "w-i-p",
  thumbnail: {
    type: "image",
    src: "/img/thumb/w-i-p.webp",
    alt: "A mockup of the design, with an example projection"
  },
  content: [{
    heading: "The Project",
    type: "md",
    text: `Work-In-Progress (yes, that’s the name) is an installation project designed and built by an interdisciplinary group at Sheridan College. Standing 6’-6” (198cm) tall with a footprint of 11’-4” (345cm) by 8’-4” (254cm), the installation consists of CNC cut and etched acrylic panels forming a faceted sculpture supported by an aluminum and steel skeleton on a plywood and steel base. Projected onto the facets are images and videos of Sheridan students engaged in the process of creating, and the works they’ve created.
The main objectives for this project were:
- Represent Sheridan students from every faculty
- Make visible the creativity that happens in Sheridan’s studios, classrooms, and workshops
- Show off Sheridan’s interdisciplinary community
      `,
    visuals: [{
      type: "vimeo",
      caption: "The WIP installation in normal projection mode.",
      src:"https://player.vimeo.com/video/862626559",
      width: 1280,
      height: 720,
      title:"Work-In-Progress projection 7min",
    },{
      type: "image",
      caption: "WIP without projection.",
      sources: [{
        src: '/img/wip/WIP_installed_side.webp',
        type: "image/webp"
      }],
      defaultSrc: '/img/wip/WIP_installed_side.webp',
      alt: "The finished WIP installation without projection. A white faceted sculpture sits on a wooden base. The sculpture comprises white acrylic panels etched with Sheridan's signature pattern and transparent orange acrylic strips over aluminum members between the panels. The base is printed with the Sheridan pattern in white on a light beige wood texture. Text on the base reads 'Creativity is the power to connect the seamingly unconnected.' -William Plomer."
    }]
  }, {
    heading: "My Roles",
    type: "md",
    text: `Over the course of the project I was responsible for a number of things. We were a small core team, so we all had to take on multiple roles and stretch our comfort zones. I’ve gathered together some highlights to give an idea of my contributions and strengths across:
- Design
- Procedural Geometry
- Fabrication
- Projection Mapping
      `,
    visual: {
      type: "image",
      caption: "An example of the geometry generated in Rhino and Grasshopper.",
      sources: [{
        src: '/img/wip/Rhinoceros-Geometry.webp',
        type: "image/webp"
      }],
      defaultSrc: '/img/wip/Rhinoceros-Geometry.webp',
      alt: "A 3D render of the facets and joining strips of the scultpure, labeled with identifying numbers."
    }
  }, {
    heading: "Design",
    type: "md",
    text: `During the conception and initial design phase of the project, I was responsible for creating 3D mockups of concepts being discussed, researching potential projection mapping solutions, and generally contributing ideas for the project. Some of the key contributions I brought to the project were using Rhino/Grasshopper for procedurally generating geometry, and TouchDesigner for projection mapping.
`,
    visual: {
      type: "image",
      caption: "The base form, with stellated spikes.",
      sources: [{
        src: '/img/wip/2-stellated-mesh.webp',
        type: "image/webp"
      }],
      defaultSrc: '/img/wip/2-stellated-mesh.webp',
      alt: "A 3D render of the sculpture, represented in grey panels."
    }
  }, {
    heading: "Projection Mapping",
    type: "md",
    text: `I created the projection mapping setup that projects onto the facets of the sculpture. I developed a dynamic loading and mapping system built on existing Touch Designer capabilities and a heavy dose of Python scripting. The program maps the assets onto the surface while accommodating for perspective distortion and loads visual media from a tagged database of both student work and archival footage. An overall theme determines which discipline of students the work is pulled from, so that each discipline has a chance to take over the whole sculpture for a time before the next group cycles in. Within each discipline, every facet goes through its own cycle of videos, fading between them and loading the next when finished. Designated groups of facets can also join together to display clips at a larger size. Occasionally, the entire sculpure will be taken over by one clip and dissolve back into individual facets when the clip ends.
`,
    visuals: [{
      type: "vimeo",
      caption: "The finished WIP installation with abstract colourful videos projected onto its facets.",
      src:"https://player.vimeo.com/video/862626538",
      width: 1280,
      height: 720,
      title:"WIP Installation Projection Mapping Demo",
    }, {
      type: "image",
      caption: "Projection mapping in progress.",
      sources: [{
        src: '/img/wip/WIP_mapping.webp',
        type: "image/webp"
      }],
      defaultSrc: '/img/wip/WIP_mapping.webp',
      alt: "WIP installation with numbers and coloured dots on the facets."
    }]
  }, {
    heading: "Procedural Geometry",
    type: "md",
    text: `I used Rhinoceros 3D and Grasshopper (a plugin for Rhino) for a wide variety of purposes over the course of this project.
##### Data Representation
One of the core concepts of this project was using the form of the sculpture to somehow represent Sheridan’s students. We decided to do this by equating the number of facets of the sculpture to the number of degree, diploma, and certificate granting programs at Sheridan. I used Rhino 3D to take an abstract mesh form, and reduce it to the desired number of faces, in order to create a faceted, 3D representation of the data at the core of the design. We used this a seed to generate the rest of the geometry for the sculpture.
##### Generating Geometry
Using the faceted base form, I created a Grasshopper network to generate the geometry we would use to fabricate the pieces of the sculpture, and that will power the projection mapping. Grasshopper allowed me to use visual programming, creating a network of connected nodes to perform mathematical and geometric transformations, growing spikes out of the existing form. These ends of these spikes act as windows into the sculpture, allowing people to see the inner skeleton. I also generated curved outlines of the panels, incorporating offsets to allow for the skeleton that would support them.
##### Data Extraction
Also with Grasshopper, I created networks that extract data from the 3D model to inform our design process—things like measurements, dimensions, running lengths, angles between facets, and other data. We used this data to revise the base form, and to create budget estimates for materials and fabrication. Because this data was extracted automatically, we were able to revise our estimates immediately when we made any changes to the model.
##### Part Labels and Manifests
In order to organize our production and assembly, I create a system in Grasshopper to label each piece generated, and output spreadsheets of each piece, including dimensions, bend angles, and how it connects to other pieces.
`,
    visuals: [{
      type: "vimeo",
      caption: "The generated geometry changing according to a parameter being changed.",
      src:"https://player.vimeo.com/video/285653692",
      width: 1280,
      height: 720,
      title:"WIP Grasshopper Demonstration",
    },{
      type: "image",
      caption: "The full Grasshopper network, including geometry generation, data extraction, and vector file export.",
      sources: [{
        src: '/img/wip/GrasshopperArtboard.webp',
        type: "image/webp"
      }],
      defaultSrc: '/img/wip/GrasshopperArtboard.webp',
      alt: "A graph of nodes connectedby lines, representing the flow of data through the Grasshopper interface.",
      style:{backgroundColor: "white"}
    }]
  }, {
    heading: "Fabrication",
    type: "md",
    text: `I was responsible for creating vector files for CNC, laser, and waterjet cutting, as well as cut lists for aluminum, and parts manifests for custom bent aluminum brackets.
##### CNC
I generated the CNC vector files in Grasshopper from the 3D model, and then exported them to Illustrator where I and the other team members prepared them by adding the etch pattern and panel hole locations.
##### Laser and Waterjet
The laser and waterjet files I generated with p5.js using data extracted from the model with Grasshopper.
##### Paper Mockups
In order to clearly show the design, we made a number of small-scale paper models of the sculpture. I exported from Rhino/Grasshopper an unfolded version of the 3D model, which I took into Illustrator to turn into a papercraft template. We printed the templates, laser-cut the outlines and score-lines, and then folded and glued them to create accurate, full-colour scale models. We used these models for presentations, and to test projection mapping.
##### 3D Printed Mockups
I 3D printed a few versions of the design for a scale model of the space, in order to show the different sizes we were proposing for the sculpture. I also plan on using a larger 3D print to test the final projection mapping setup. `,
    visual: {
      type: "image",
      caption: "The flow of data from model to fabrication.",
      sources: [{
        src: '/img/wip/DimensionsExtraction.webp',
        type: "image/webp"
      }],
      defaultSrc: '/img/wip/DimensionsExtraction.webp',
      alt: "A diagram showing the flow of data through stages of design and fabrication.",
      style:{backgroundColor: "white"}
    }
  },{
    heading: "Proof of Concept",
    type: "md",
    text: `For pitch presentations and in-progress gallery exhibits, I created a partial implementation that projects onto a prototype fragment of the sculpture.
`,
    visuals: [{
      type: "vimeo",
      caption: "A prototype projection mapping running on a paper model of the WIP installation.",
      src:"https://player.vimeo.com/video/285654120",
      width: 1280,
      height: 720,
      title:"WIP Installation Projection Mapping Demo",
    },{
      type: "image",
      caption: "Projection mapping running on a prototype fragment.",
      sources: [{
        src: '/img/wip/Prototype-Projection-Mapping.webp',
        type: "image/webp"
      }],
      defaultSrc: '/img/wip/Prototype-Projection-Mapping.webp',
      alt: "Several facets from the sculpture mounted on a stand in an art gallery, with projection on each facet."
    }]
  },  {
    heading:"The Team",
    type: "md",
    text: `The core team consisted of Claire Ironside (Interaction Design professor), Doug Donald (Illustration professor), Duncan Foy (Photography graduate), Alex Geddie (Faculty Technologist), and me. We’ve also relied on the help of David Hillman (Welding), Peter Fleming (Furniture), and Sarah Lindsay (Furniture).
`,
    visual: {
      type: "image",
      caption: "Left to right: Doug Donald, Scott Wilson, Claire Ironside. Photo credit Duncan Foy.",
      sources: [{
        src: '/img/wip/WIP-Team.webp',
        type: "image/webp"
      }],
      defaultSrc: '/img/wip/WIP-Team.webp',
      alt: "Three people holding and maneuvering pieces of a papercraft model of the WIP sculpture."
    }
  }]
}
